import React, { useState } from 'react';
import {
  Row,
  Col,
  Typography,
  Form as AntdForm,
  Input,
  DatePicker,
  Checkbox,
  Select,
  Radio,
  Button,
} from 'antd';
import '../../../styles/Global.less';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { RadioChangeEvent } from 'antd/lib/radio';

const { Title, Text } = Typography;
const { TextArea } = Input;

const HaroldSpeakingEngagements: React.FC = () => {
  // #region State, Props, etc...

  const [dateRequired, setDateRequired] = useState(true);
  const [otherFormat, setOtherFormat] = useState(false);
  const [practiceSessionRequired, setPracticeSessionRequired] = useState(false);
  const [otherMaterialsRequired, setOtherMaterialsRequired] = useState(false);
  const [form] = AntdForm.useForm();

  // #endregion

  // #region General Component State Functions

  const onDateCheckboxChange = (e: CheckboxChangeEvent) => {
    setDateRequired(!e.target.checked);
    form.resetFields(['date']);
  };

  const onFormatSelectChange = (e: string) => {
    if (e === 'Other') setOtherFormat(true);
    else setOtherFormat(false);
  };

  const onPracticeSessionRadioChange = (e: RadioChangeEvent) => {
    if (e.target.value === 1) setPracticeSessionRequired(true);
    else setPracticeSessionRequired(false);
  };

  const onAdditionalMaterialsSelectChange = (e: string[]) => {
    if (e.includes('Other')) setOtherMaterialsRequired(true);
    else setOtherMaterialsRequired(false);
  };

  // #endregion

  // #region Render section

  return (
    <Col span={14}>
      <Title>Harold Speaking Engagements</Title>
      <Row className="mb-1">
        <Text type="danger" strong>
          * Required Field
        </Text>
      </Row>
      <AntdForm form={form} size="large">
        <Row>
          <Title level={4} className="mb-0">
            Name of requested speaker for your event
            <span className="required">*</span>
          </Title>
        </Row>
        <Row>
          <Text type="secondary">
            Please type the First & Last Name of speaker(s)/presenter(s).
          </Text>
        </Row>
        <Row className="mt-0_5" gutter={16}>
          <Col span={12}>
            <AntdForm.Item
              name="speakerFirstName"
              rules={[{ required: true, message: 'Please enter first name!' }]}
            >
              <Input placeholder="First Name" />
            </AntdForm.Item>
          </Col>
          <Col span={12}>
            <AntdForm.Item
              name="speakerLastName"
              rules={[{ required: true, message: 'Please enter last name!' }]}
            >
              <Input placeholder="Last Name" />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Who is the requesting agency/group?
            <span className="required">*</span>
          </Title>
        </Row>
        <Row>
          <Text type="secondary">
            Please include the agency/group name here.
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={24}>
            <AntdForm.Item
              name="agency"
              rules={[{ required: true, message: 'Please enter a value!' }]}
            >
              <Input placeholder="Enter requesting agency/group..." />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Who is the agency/group contact person?
            <span className="required">*</span>
          </Title>
        </Row>
        <Row>
          <Text type="secondary">
            Please include the contact persons name and email here.
          </Text>
        </Row>
        <Row className="mt-0_5" gutter={16}>
          <Col span={12}>
            <AntdForm.Item
              name="agencyFirstName"
              rules={[
                { required: true, message: 'Please enter a first name!' },
              ]}
            >
              <Input placeholder="First Name" />
            </AntdForm.Item>
          </Col>
          <Col span={12}>
            <AntdForm.Item
              name="agencyLastName"
              rules={[{ required: true, message: 'Please enter a last name!' }]}
            >
              <Input placeholder="Last Name" />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <AntdForm.Item
              name="agencyEmail"
              rules={[
                {
                  type: 'email',
                  message: 'Please enter a valid email!',
                },
                {
                  required: true,
                  message: 'Please enter an email!',
                },
              ]}
            >
              <Input placeholder="Email" />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4}>
            What topic(s) would you like the speaker to address?
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Col span={24}>
            <AntdForm.Item
              name="topics"
              rules={[{ required: true, message: 'Please enter topics!' }]}
            >
              <TextArea rows={8} placeholder="Enter topics..." />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Date
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">Please provide the date of the event.</Text>
        </Row>
        <Row className="mt-0_5" gutter={24} align="middle">
          <Col>
            <AntdForm.Item
              name="date"
              rules={[
                {
                  type: 'object',
                  required: dateRequired,
                  message: 'Please enter a date!',
                },
              ]}
            >
              <DatePicker
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                disabled={!dateRequired}
              />
            </AntdForm.Item>
          </Col>
          <Col className="mb-1_5">
            <Text strong>- OR -</Text>
          </Col>
          <Col>
            <AntdForm.Item>
              <Checkbox onChange={onDateCheckboxChange}>TBD</Checkbox>
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Format
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">Please identify the format of the event.</Text>
        </Row>
        <Row className="mt-0_5" gutter={16}>
          <Col span={12}>
            <AntdForm.Item
              name="format"
              rules={[{ required: true, message: 'Please select an option!' }]}
            >
              <Select placeholder="Format" onChange={onFormatSelectChange}>
                <Select.Option value="PowerPoint">PowerPoint</Select.Option>
                <Select.Option value="Webinar">Webinar</Select.Option>
                <Select.Option value="Discussion">Discussion</Select.Option>
                <Select.Option value="Q&A only">Q&A only</Select.Option>
                <Select.Option value="Speech">Speech</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </AntdForm.Item>
          </Col>
          {otherFormat && (
            <Col span={12}>
              <AntdForm.Item
                name="otherFormat"
                rules={[{ required: true, message: 'Please enter a value!' }]}
              >
                <Input placeholder="Enter other format..." />
              </AntdForm.Item>
            </Col>
          )}
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Platform (e.g. - WebEx, Go2Meeting, Zoom, MS Teams etc.)
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">
            What platform will the event take place on?
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={24}>
            <AntdForm.Item
              name="platform"
              rules={[{ required: true, message: 'Please enter a platform!' }]}
            >
              <Input placeholder="Enter platform..." />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Access to the event
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">
            Please provide instructions on how to access the event here.
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={24}>
            <AntdForm.Item
              name="access"
              rules={[
                { required: true, message: 'Please enter instructions!' },
              ]}
            >
              <TextArea
                rows={8}
                placeholder="Enter event access instructions..."
              />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Length of Presentation (In Minutes)
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">
            How long are you requesting the speaker to present/speak?
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={12}>
            <AntdForm.Item
              name="presentationLength"
              rules={[{ required: true, message: 'Please enter a length!' }]}
            >
              <Input
                placeholder="Enter length of presentation in minutes..."
                type="number"
              />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Total Length of Event (In Minutes)
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">
            How long are you requesting the speaker to present/speak?
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={12}>
            <AntdForm.Item
              name="eventLength"
              rules={[{ required: true, message: 'Please enter a length!' }]}
            >
              <Input
                placeholder="Enter total length of event in minutes..."
                type="number"
              />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4}>
            Are there any practice sessions needed to attend?
          </Title>
          <span className="required">*</span>
        </Row>
        <AntdForm.Item
          name="practiceSessions"
          rules={[{ required: true, message: 'Please select an option!' }]}
        >
          <Radio.Group onChange={onPracticeSessionRadioChange}>
            <Radio value={1}>Yes</Radio>
            <Radio value={0}>No</Radio>
          </Radio.Group>
        </AntdForm.Item>
        {practiceSessionRequired && (
          <>
            <Row>
              <Title level={4}>
                Please indicate the date & time of the practice session.
              </Title>
              <span className="required">*</span>
            </Row>
            <Row>
              <AntdForm.Item
                name="practiceSessionDate"
                rules={[
                  {
                    type: 'object',
                    required: dateRequired,
                    message: 'Please enter a date!',
                  },
                ]}
              >
                <DatePicker
                  showTime
                  format="MM/DD/YYYY hh:mm A"
                  placeholder="MM/DD/YYYY 00:00 AM"
                />
              </AntdForm.Item>
            </Row>
          </>
        )}
        <Row>
          <Title level={4}>Who will control the presentation slides?</Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Col span={12}>
            <AntdForm.Item
              name="control"
              rules={[{ required: true, message: 'Please select an option!' }]}
            >
              <Select placeholder="Select an option...">
                <Select.Option value="Presenter/Speaker control">
                  Presenter/Speaker control
                </Select.Option>
                <Select.Option value="Organization Control">
                  Organization control
                </Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Event Description
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">
            Please provide the event description here including information on
            the intended audience.
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={24}>
            <AntdForm.Item
              name="description"
              rules={[
                { required: true, message: 'Please enter a description!' },
              ]}
            >
              <TextArea rows={8} placeholder="Enter event description..." />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4}>
            Do you need any of the following additional materials? (Headshot,
            Bio, etc.)
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Col span={12}>
            <AntdForm.Item
              name="additionalMaterials"
              rules={[{ required: true, message: 'Please select an option!' }]}
            >
              <Select
                mode="multiple"
                allowClear
                maxTagCount={3}
                showArrow
                showSearch={false}
                placeholder="Select an option..."
                onChange={onAdditionalMaterialsSelectChange}
              >
                <Select.Option value="Headshot">Headshot</Select.Option>
                <Select.Option value="Bio">Bio</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </AntdForm.Item>
          </Col>
        </Row>
        {otherMaterialsRequired && (
          <>
            <Row>
              <Title level={4}>
                If &quot;Other&quot; additional materials is needed please list
                them below
              </Title>
              <span className="required">*</span>
            </Row>
            <Row className="mt-0_5">
              <Col span={24}>
                <AntdForm.Item
                  name="otherAdditionalMaterials"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter additional materials!',
                    },
                  ]}
                >
                  <TextArea
                    rows={8}
                    placeholder="Enter other additional materials..."
                  />
                </AntdForm.Item>
              </Col>
            </Row>
          </>
        )}
        <Row>
          <Title level={4} className="mb-0">
            Promotional Tools (e.g. Blogs, Twitter, etc.)
          </Title>
        </Row>
        <Row>
          <Text type="secondary">
            Please provide any promotional materials that will be provided for
            this event here.
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={24}>
            <AntdForm.Item name="promotionalTools">
              <TextArea rows={8} placeholder="Enter promotional tools..." />
            </AntdForm.Item>
          </Col>
        </Row>
        <Row>
          <Title level={4} className="mb-0">
            Email Address
          </Title>
          <span className="required">*</span>
        </Row>
        <Row>
          <Text type="secondary">
            Please enter Marissa.Robinson@hhs.gov in this field.
          </Text>
        </Row>
        <Row className="mt-0_5">
          <Col span={24}>
            <AntdForm.Item
              name="email"
              rules={[
                {
                  type: 'email',
                  message: 'Please enter a valid email!',
                },
                {
                  required: true,
                  message: 'Please enter an email!',
                },
              ]}
              initialValue="Marissa.Robinson@hhs.gov"
            >
              <Input placeholder="Email" />
            </AntdForm.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </AntdForm>
    </Col>
  );

  // #endregion
};

export default HaroldSpeakingEngagements;
